import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useLoad } from "@/hooks/useLoad";
import { api } from "@/lib/api";
import { AREAS } from "@/components/app/GrowthDialogs";

const RES = [["money", "Capital deployed", (v) => `$${Math.round(v).toLocaleString()}`], ["time", "Time (hours)", (v) => `${Math.round(v).toLocaleString()} h`], ["people", "People (headcount)", (v) => `${Math.round(v * 10) / 10}`]];
const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#94A3B8"];
const area = (k) => AREAS.find((a) => a[0] === k)?.[1] || k;
const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleString(undefined, { month: "short", year: "2-digit", timeZone: "UTC" });

const Bars = ({ data, fmt, label, testId }) => {
  const rows = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div data-testid={testId}><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{label}</div>
      {rows.length ? <div className="space-y-2">{rows.map(([k, v], i) => (
        <div key={k} className="text-sm"><div className="flex justify-between gap-2"><span className="truncate">{label.includes("function") ? area(k) : k}</span><span className="font-mono text-xs">{fmt(v)}</span></div>
          <div className="h-2 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(v / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></div></div>))}</div>
        : <p className="text-sm text-muted-foreground">Nothing deployed.</p>}
    </div>
  );
};

const Trend = ({ months, res, fmt, sel, setSel }) => {
  const max = Math.max(1, ...months.map((m) => m[res].total));
  return (
    <div className="flex items-end gap-2 h-28" data-testid={`mix-trend-${res}`}>
      {months.map((m) => (
        <button key={m.month} onClick={() => setSel(m.month)} title={fmt(m[res].total)} data-testid={`mix-month-${res}-${m.month}`}
          className={`flex-1 flex flex-col items-center justify-end h-full group ${sel === m.month ? "" : "opacity-60 hover:opacity-100"}`}>
          <div className={`w-full rounded-t-md ${sel === m.month ? "bg-primary" : "bg-primary/50"}`} style={{ height: `${Math.max(3, (m[res].total / max) * 100)}%` }} />
          <span className="text-[10px] text-muted-foreground mt-1">{monthLabel(m.month)}</span>
        </button>))}
    </div>
  );
};

export const AllocationMix = () => {
  const state = useLoad(() => api.get("/allocations/mix?months=6").then((r) => r.data));
  const [sel, setSel] = useState(null);
  if (state.loading) return <div className="surface p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const d = state.data;
  if (!d) return null;
  const month = sel || d.months[d.months.length - 1].month;
  const cur = d.months.find((m) => m.month === month);
  return (
    <div className="space-y-5" data-testid="allocation-mix">
      {RES.map(([k, label, fmt]) => (
        <div key={k} className="surface p-5" data-testid={`mix-${k}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4"><div className="font-semibold">{label}</div>
            <div className="text-sm"><span className="text-muted-foreground">{monthLabel(month)}:</span> <b className="font-mono" data-testid={`mix-total-${k}`}>{fmt(cur[k].total)}</b></div></div>
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr_1fr]">
            <Trend months={d.months} res={k} fmt={fmt} sel={month} setSel={setSel} />
            <Bars data={cur[k].by_business} fmt={fmt} label="By business" testId={`mix-business-${k}`} />
            <Bars data={cur[k].by_function} fmt={fmt} label="By function" testId={`mix-function-${k}`} />
          </div>
        </div>))}
      {d.roi.length > 0 && (
        <div className="surface p-5" data-testid="mix-roi">
          <div className="font-semibold mb-3">Return on capital by business</div>
          <div className="divide-y divide-border">{d.roi.map((r) => (
            <div key={r.business} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`mix-roi-${r.business}`}>
              <span className="flex-1 min-w-[160px] font-medium">{r.business}</span>
              <span className="text-xs text-muted-foreground">${Math.round(r.invested).toLocaleString()} in → ${Math.round(r.returned).toLocaleString()} back</span>
              <span className="font-mono w-16 text-right">{r.roi_pct != null ? `${r.roi_pct}%` : "—"}</span>
              {r.vs_forecast_pct != null && <span className={`text-xs sm:w-28 sm:text-right ${r.vs_forecast_pct >= 0 ? "text-emerald-500" : "text-red-500"}`}>{r.vs_forecast_pct >= 0 ? "+" : ""}{r.vs_forecast_pct}% vs forecast</span>}
            </div>))}</div>
        </div>)}
      <p className="text-xs text-muted-foreground">{d.notes}</p>
    </div>
  );
};
